import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  makeStyles,
  Grid,
  CircularProgress,
  Typography,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Chip,
  Divider,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import GetAppIcon from "@material-ui/icons/GetApp";
import { BASE_URL } from "utils/constants";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#f5f5f5",
    fontFamily: "'Public Sans', sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "20px 32px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
    },
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 4,
  },
  companyLogo: {
    height: 48,
    maxWidth: 160,
    objectFit: "contain",
  },
  companyName: {
    fontWeight: 700,
    fontSize: 20,
    color: "#1a1a1a",
    fontFamily: "'Public Sans', sans-serif",
  },
  companyCity: {
    fontSize: 14,
    color: "#666666",
    marginTop: 2,
    fontFamily: "'Public Sans', sans-serif",
  },
  metaBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid #e0e0e0",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  metaLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  photoCount: {
    fontWeight: 600,
    fontSize: 15,
    color: "#333333",
    fontFamily: "'Public Sans', sans-serif",
  },
  expiresText: {
    fontSize: 13,
    color: "#888888",
    fontFamily: "'Public Sans', sans-serif",
  },
  downloadAllBtn: {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    textTransform: "none",
    fontFamily: "'Public Sans', sans-serif",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  gridContainer: {
    padding: "24px 32px",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
    },
  },
  photoCard: {
    position: "relative",
    paddingTop: "75%", // 4:3 aspect ratio
    overflow: "hidden",
    borderRadius: 8,
    cursor: "pointer",
    backgroundColor: "#e0e0e0",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    "&:hover $photoOverlay": {
      opacity: 1,
    },
  },
  photoImg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.2s ease",
  },
  photoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.25)",
    opacity: 0,
    transition: "opacity 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  videoBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    fontFamily: "'Public Sans', sans-serif",
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  footer: {
    textAlign: "center",
    padding: "16px",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#ffffff",
  },
  footerText: {
    fontSize: 12,
    color: "#aaaaaa",
    fontFamily: "'Public Sans', sans-serif",
  },
  // Error states
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "40px 24px",
    textAlign: "center",
  },
  errorTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#333333",
    marginBottom: 12,
    fontFamily: "'Public Sans', sans-serif",
  },
  errorBody: {
    fontSize: 15,
    color: "#666666",
    maxWidth: 480,
    lineHeight: 1.6,
    fontFamily: "'Public Sans', sans-serif",
  },
  errorHeader: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "20px 32px",
  },
  // Lightbox
  lightboxDialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#111111",
      maxWidth: "95vw",
      maxHeight: "95vh",
      width: "auto",
      margin: 16,
    },
  },
  lightboxTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "#000000",
  },
  lightboxCounter: {
    color: "#ffffff",
    fontSize: 14,
    fontFamily: "'Public Sans', sans-serif",
  },
  lightboxNav: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  navBtn: {
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
    },
    "&:disabled": {
      color: "rgba(255,255,255,0.3)",
    },
  },
  closeBtn: {
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
    },
  },
  lightboxImgWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    maxHeight: "70vh",
    overflow: "hidden",
    backgroundColor: "#000000",
  },
  lightboxImg: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    display: "block",
  },
  lightboxFooter: {
    backgroundColor: "#1a1a1a",
    padding: "12px 16px",
  },
  lightboxMeta: {
    color: "#cccccc",
    fontSize: 13,
    fontFamily: "'Public Sans', sans-serif",
    marginBottom: 6,
  },
  lightboxDescription: {
    color: "#aaaaaa",
    fontSize: 13,
    fontFamily: "'Public Sans', sans-serif",
    marginBottom: 8,
    fontStyle: "italic",
  },
  lightboxTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 10,
  },
  tag: {
    backgroundColor: "rgba(255,255,255,0.12)",
    color: "#dddddd",
    fontSize: 11,
    height: 22,
    fontFamily: "'Public Sans', sans-serif",
  },
  downloadPhotoBtn: {
    color: "#90caf9",
    textTransform: "none",
    fontFamily: "'Public Sans', sans-serif",
    padding: 0,
    fontSize: 13,
    "&:hover": {
      backgroundColor: "transparent",
      color: "#64b5f6",
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: 16,
  },
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ErrorHeader({ companyName, companyLogo, classes }) {
  return (
    <Box className={classes.errorHeader}>
      <Box className={classes.logoRow}>
        {companyLogo && (
          <img
            src={companyLogo}
            alt={companyName}
            className={classes.companyLogo}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}
        {companyName && (
          <Typography className={classes.companyName}>{companyName}</Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PublicSharePage() {
  const { token } = useParams();
  const classes = useStyles();

  const [albumData, setAlbumData] = useState(null);
  const [loading, setLoading] = useState(true);
  // errorState: null | "expired" | "revoked" | "not_found" | "all_trashed"
  const [errorState, setErrorState] = useState(null);
  const [errorMeta, setErrorMeta] = useState({});
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // ------------------------------------------------------------------
  // Fetch album — uses native fetch() to avoid axios global Authorization
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!token) {
      setErrorState("not_found");
      setLoading(false);
      return;
    }

    const url = `${BASE_URL}/public/share/${token}`;

    fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));

        if (response.status === 404) {
          setErrorState("not_found");
          return;
        }

        if (response.status === 410) {
          const errCode = body.error;
          if (errCode === "link_revoked") {
            setErrorState("revoked");
            setErrorMeta({ company_name: body.company_name, company_logo: body.company_logo });
          } else if (errCode === "link_expired") {
            setErrorState("expired");
            setErrorMeta({
              company_name: body.company_name,
              company_logo: body.company_logo,
              expired_at: body.expired_at,
            });
          } else {
            setErrorState("not_found");
          }
          return;
        }

        if (response.ok) {
          if (body.all_trashed || (Array.isArray(body.photos) && body.photos.length === 0 && body.all_trashed)) {
            if (body.all_trashed) {
              setErrorState("all_trashed");
              setErrorMeta({ company_name: body.company_name });
              return;
            }
          }
          setAlbumData(body);
        } else {
          setErrorState("not_found");
        }
      })
      .catch(() => {
        setErrorState("not_found");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  // ------------------------------------------------------------------
  // Keyboard navigation for lightbox
  // ------------------------------------------------------------------
  const photos = albumData?.photos || [];

  const handleKeyDown = useCallback(
    (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "ArrowRight") {
        setLightboxIndex((prev) => (prev < photos.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowLeft") {
        setLightboxIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Escape") {
        setLightboxIndex(null);
      }
    },
    [lightboxIndex, photos.length]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // ------------------------------------------------------------------
  // Download all ZIP
  // ------------------------------------------------------------------
  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const url = `${BASE_URL}/public/share/${token}/zip`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${albumData?.project_name || "photos"}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (_err) {
      // silently fail — button returns to normal state
    } finally {
      setDownloading(false);
    }
  };

  // ------------------------------------------------------------------
  // Download single photo
  // ------------------------------------------------------------------
  const handleDownloadPhoto = (photo) => {
    const url = `${BASE_URL}/public/share/${token}/photo/${photo.id}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = photo.photo_name || "photo";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Error: not found / generic
  // ------------------------------------------------------------------
  if (errorState === "not_found") {
    return (
      <Box className={classes.root}>
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            This link is no longer available.
          </Typography>
          <Typography className={classes.errorBody}>
            The shared photo album has been removed. Please contact the person who sent you this link.
          </Typography>
        </Box>
        <Box className={classes.footer}>
          <Typography className={classes.footerText}>
            Shared via X-Scope · Powered by SyncKonnect
          </Typography>
        </Box>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Error: revoked
  // ------------------------------------------------------------------
  if (errorState === "revoked") {
    return (
      <Box className={classes.root}>
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            This link is no longer available.
          </Typography>
          <Typography className={classes.errorBody}>
            The shared photo album has been removed. Please contact the person who sent you this link.
          </Typography>
        </Box>
        <Box className={classes.footer}>
          <Typography className={classes.footerText}>
            Shared via X-Scope · Powered by SyncKonnect
          </Typography>
        </Box>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Error: expired (shows company branding)
  // ------------------------------------------------------------------
  if (errorState === "expired") {
    return (
      <Box className={classes.root}>
        <ErrorHeader
          companyName={errorMeta.company_name}
          companyLogo={errorMeta.company_logo}
          classes={classes}
        />
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            This link has expired.
          </Typography>
          <Typography className={classes.errorBody}>
            The photos shared via this link are no longer available. If you need
            access, please contact the roofing company.
          </Typography>
        </Box>
        <Box className={classes.footer}>
          <Typography className={classes.footerText}>
            Shared via X-Scope · Powered by SyncKonnect
          </Typography>
        </Box>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Error: all photos trashed
  // ------------------------------------------------------------------
  if (errorState === "all_trashed") {
    return (
      <Box className={classes.root}>
        <Box className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            No photos available.
          </Typography>
          <Typography className={classes.errorBody}>
            The photos in this album are no longer available. Please contact{" "}
            {errorMeta.company_name || "the roofing company"} for more
            information.
          </Typography>
        </Box>
        <Box className={classes.footer}>
          <Typography className={classes.footerText}>
            Shared via X-Scope · Powered by SyncKonnect
          </Typography>
        </Box>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Active album
  // ------------------------------------------------------------------
  const photoCount = photos.length;
  const TOO_MANY_FILES_THRESHOLD = 200;
  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;

  return (
    <Box className={classes.root}>
      {/* ---- Header ---- */}
      <Box className={classes.header}>
        <Box className={classes.logoRow}>
          {albumData.company_logo && (
            <img
              src={albumData.company_logo}
              alt={albumData.company_name}
              className={classes.companyLogo}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <Typography className={classes.companyName}>
            {albumData.company_name}
          </Typography>
        </Box>
        {albumData.project_address_city && (
          <Typography className={classes.companyCity}>
            {albumData.project_address_city}
          </Typography>
        )}

        <Box className={classes.metaBar}>
          <Box className={classes.metaLeft}>
            <Typography className={classes.photoCount}>
              {photoCount} {photoCount === 1 ? "photo" : "photos"} shared with you
            </Typography>
            {albumData.expires_at && (
              <Typography className={classes.expiresText}>
                Expires: {formatDate(albumData.expires_at)}
              </Typography>
            )}
          </Box>

          {photoCount > 0 && (
            photoCount > TOO_MANY_FILES_THRESHOLD ? (
              <Typography style={{ fontSize: 13, color: "#e57373" }}>
                Too many files to download at once.
              </Typography>
            ) : (
              <Button
                variant="contained"
                className={classes.downloadAllBtn}
                startIcon={<GetAppIcon />}
                onClick={handleDownloadZip}
                disabled={downloading}
              >
                {downloading ? "Preparing…" : "Download all (ZIP)"}
              </Button>
            )
          )}
        </Box>
      </Box>

      {/* ---- Photo grid ---- */}
      <Box className={classes.gridContainer}>
        <Grid container spacing={2}>
          {photos.map((photo, index) => (
            <Grid item xs={12} sm={6} md={4} key={photo.id}>
              <Box
                className={classes.photoCard}
                onClick={() => setLightboxIndex(index)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") setLightboxIndex(index);
                }}
              >
                <img
                  src={photo.thumbnail_url || photo.blob_url}
                  alt={photo.photo_name}
                  className={classes.photoImg}
                  loading="lazy"
                />
                {photo.type === "video" && (
                  <span className={classes.videoBadge}>Video</span>
                )}
                <Box className={classes.photoOverlay} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ---- Footer ---- */}
      <Box className={classes.footer}>
        <Typography className={classes.footerText}>
          Shared via X-Scope · Powered by SyncKonnect
        </Typography>
      </Box>

      {/* ---- Lightbox ---- */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        maxWidth="lg"
        fullWidth
        className={classes.lightboxDialog}
        PaperProps={{ style: { backgroundColor: "#111111" } }}
      >
        {currentPhoto && (
          <>
            {/* Top bar */}
            <Box className={classes.lightboxTopBar}>
              <IconButton
                className={classes.closeBtn}
                onClick={() => setLightboxIndex(null)}
                size="small"
              >
                <CloseIcon />
              </IconButton>

              <Box className={classes.lightboxNav}>
                <IconButton
                  className={classes.navBtn}
                  onClick={() => setLightboxIndex((p) => p - 1)}
                  disabled={lightboxIndex === 0}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.lightboxCounter}>
                  {lightboxIndex + 1} of {photoCount}
                </Typography>
                <IconButton
                  className={classes.navBtn}
                  onClick={() => setLightboxIndex((p) => p + 1)}
                  disabled={lightboxIndex === photoCount - 1}
                  size="small"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Image */}
            <DialogContent style={{ padding: 0, backgroundColor: "#000000" }}>
              <Box className={classes.lightboxImgWrap}>
                {currentPhoto.type === "video" ? (
                  <video
                    src={currentPhoto.blob_url}
                    controls
                    style={{ maxWidth: "100%", maxHeight: "70vh" }}
                  />
                ) : (
                  <img
                    src={currentPhoto.blob_url}
                    alt={currentPhoto.photo_name}
                    className={classes.lightboxImg}
                  />
                )}
              </Box>
            </DialogContent>

            {/* Footer */}
            <Box className={classes.lightboxFooter}>
              <Typography className={classes.lightboxMeta}>
                {formatDateTime(currentPhoto.create_time)}
                {currentPhoto.uploader_name ? ` · ${currentPhoto.uploader_name}` : ""}
              </Typography>

              {currentPhoto.description ? (
                <Typography className={classes.lightboxDescription}>
                  {currentPhoto.description}
                </Typography>
              ) : null}

              {Array.isArray(currentPhoto.tags) && currentPhoto.tags.length > 0 && (
                <Box className={classes.lightboxTags}>
                  {currentPhoto.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      className={classes.tag}
                    />
                  ))}
                </Box>
              )}

              <Divider style={{ backgroundColor: "rgba(255,255,255,0.1)", margin: "8px 0" }} />

              <Button
                className={classes.downloadPhotoBtn}
                startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
                onClick={() => handleDownloadPhoto(currentPhoto)}
                disableRipple
              >
                Download this photo
              </Button>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default PublicSharePage;
